import { dispatchDeskNavigation, type DeskViewMode } from '@/msgdeskRuntime/deskNavigation'
import {
  isStandardEditorSegment,
  standardEditorRouteSegments,
  standardEditorSegmentForMode,
  standardFolderRouteSegments,
  type StandardEditorMode,
} from '../routing/standard'

export type StandardRouteMode = DeskViewMode

const routeSegmentByMode: Record<StandardRouteMode, string> = {
  msg: '',
  answers: 'answers',
  submsges: 'msges',
  attachments: 'atts',
  favs: 'favs',
  drafts: 'drafts',
  trash: 'trash',
}

const modeByRouteSegment: Record<string, StandardRouteMode> = {
  answers: 'answers',
  msges: 'submsges',
  atts: 'attachments',
  favs: 'favs',
  drafts: 'drafts',
  trash: 'trash',
}

const standardRouteSegments = new Set<string>([
  ...standardFolderRouteSegments,
  ...standardEditorRouteSegments,
])

export function standardModeFromSegment(segment: string | undefined): StandardRouteMode {
  if (!segment) return 'msg'
  return modeByRouteSegment[segment.toLowerCase()] || 'msg'
}

export function standardModeFromPathname(pathname: string): StandardRouteMode {
  return standardModeFromSegment(pathname.split('/').filter(Boolean).pop())
}

function standardBasePath(pathname: string): string {
  const parts = pathname.split('/').filter(Boolean)
  if (parts.length && standardRouteSegments.has(parts[parts.length - 1].toLowerCase())) parts.pop()
  return `/${parts.join('/')}` || '/'
}

export function standardPathForMode(mode: StandardRouteMode, pathname: string, search = '', hash = ''): string {
  const base = standardBasePath(pathname)
  const segment = routeSegmentByMode[mode]
  return `${segment ? `${base}/${segment}` : base}${search}${hash}`
}

export function standardPathForEditorMode(mode: StandardEditorMode, pathname: string, search = '', hash = ''): string {
  const base = standardBasePath(pathname)
  return `${base}/${standardEditorSegmentForMode(mode)}${search}${hash}`
}

export function isStandardEditorPathname(pathname: string): boolean {
  const segment = pathname.split('/').filter(Boolean).pop() || ''
  return isStandardEditorSegment(segment)
}

export function pushStandardModeUrl(mode: StandardRouteMode) {
  if (typeof window === 'undefined') return
  const nextUrl = standardPathForMode(mode, window.location.pathname, window.location.search, window.location.hash)
  const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`
  if (nextUrl !== currentUrl) window.history.pushState({ standardMode: mode }, '', nextUrl)
}

export function pushStandardEditorUrl(mode: StandardEditorMode) {
  if (typeof window === 'undefined') return
  const nextUrl = standardPathForEditorMode(mode, window.location.pathname, window.location.search, window.location.hash)
  const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`
  if (nextUrl !== currentUrl) window.history.pushState({ standardEditorMode: mode }, '', nextUrl)
}

export function replaceStandardModeUrl(mode: StandardRouteMode) {
  if (typeof window === 'undefined') return
  const nextUrl = standardPathForMode(mode, window.location.pathname, window.location.search, window.location.hash)
  window.history.replaceState({ standardMode: mode }, '', nextUrl)
}

export function applyStandardMode(mode: StandardRouteMode, target?: HTMLElement | null) {
  dispatchDeskNavigation({ mode, target: target || undefined })
}
